/**
 * Copy to clipboard, with a fallback that actually matters here.
 *
 * `navigator.clipboard` is only defined in a secure context. The onboarding
 * snippet is the single most important thing a customer ever copies, and a
 * developer testing over plain http on a LAN address, or inside an embedded
 * webview, would otherwise silently get nothing. The fallback is the old
 * `execCommand` path: deprecated, still implemented everywhere, and correct.
 */
export async function copyText(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission denied or a non secure context. Fall through.
    }
  }

  if (typeof document === 'undefined') return false;

  const area = document.createElement('textarea');
  area.value = text;
  // Keep it in the layout but invisible: a `display: none` element cannot be
  // selected, and `position: fixed` at the top avoids scrolling the page.
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.top = '0';
  area.style.left = '0';
  area.style.opacity = '0';
  area.style.pointerEvents = 'none';
  document.body.appendChild(area);

  try {
    area.select();
    area.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(area);
  }
}
